import type { ReactElement } from 'react';
import type { ProductAuthoringController } from './controller';
import type { ProductAuthoringCatalogActions, ProductAuthoringProduct } from './types';
import { ProductAuthoringLayout } from './ProductAuthoringLayout';
import { productKindSections } from '../../products/kindSections';
import { AuthoringKindSections } from './AuthoringKindSections';
import { ProductAuthoringDrawer, type ProductAuthoringCreateSeed } from './ProductDrawer';
import { useProductDrawerState } from '../../products/drawerState';
import type { ProductFormConfig } from '../../products/types';

export type DefaultProductAuthoringProps<TProduct extends ProductAuthoringProduct> = {
  controller: ProductAuthoringController<TProduct>;
  formConfig?: ProductFormConfig;
  canEdit?: boolean;
  listPriceEditable?: boolean;
  /** Live catalogue-action wiring passed through to the shared drawer. */
  catalogActions?: ProductAuthoringCatalogActions<TProduct>;
};

/**
 * Default Products and prices workspace for scalar-priced activity types:
 * flat MEDIA, SERVICE, and SMART lists over the shared kind-section chrome,
 * with optional price-list-local `ProductPrice` on each product.
 */
export function DefaultProductAuthoring<TProduct extends ProductAuthoringProduct>({
  controller,
  formConfig,
  canEdit = true,
  listPriceEditable = true,
  catalogActions,
}: DefaultProductAuthoringProps<TProduct>): ReactElement {
  const drawer = useProductDrawerState<ProductAuthoringCreateSeed>();
  const { input } = controller;
  const onEdit = canEdit ? (productId: number) => drawer.open({ mode: 'edit', productId }) : undefined;

  return (
    <ProductAuthoringLayout>
      <AuthoringKindSections
        products={input.products}
        prices={input.prices}
        sections={productKindSections}
        onEdit={onEdit}
        onCreate={canEdit ? (kind) => drawer.open({ mode: 'create', seed: { kind } }) : undefined}
      />

      <ProductAuthoringDrawer
        controller={controller}
        drawer={drawer}
        formConfig={formConfig}
        listPriceEditable={listPriceEditable}
        catalogActions={catalogActions}
        testId="default-product-authoring-drawer"
      />
    </ProductAuthoringLayout>
  );
}
